<script setup>
import { computed, ref } from "vue";
import notifyUser from "@/helpers/notifyUser.js";
import CertificateListItem from "@/Components/documents/CertificateListItem.vue";
import BaseSearchInput from "@/Components/UI/search/BaseSearchInput.vue";

const props = defineProps({
    list: {
        type: Array,
        required: true,
    },
    chosen: {
        required: true,
    },
    full: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(["update:chosen"]);

// The Clarity recording that motivated this box held exactly 4 namesake
// certificates (Чернов, Чернова Елена, Чернова Кристина, Крайнюков) and that
// was already enough to cause mis-clicks. The threshold sits at that observed
// size so the fix covers the case that generated the report; a 2-certificate
// list stays as plain as it is today.
const SEARCH_THRESHOLD = 4;
const showSearch = computed(() => props.list.length >= SEARCH_THRESHOLD);

const query = ref("");

// Only the fields CertificateListItem actually renders: the title (CN), the
// surname/given-name line, and the email fallback line shown when SN/G are
// absent. ИНН/СНИЛС are skipped — they only render under the `full` prop and
// aren't what a person types to find a name among namesakes.
const matchesQuery = (certificate, needle) => {
    const subject = certificate.subject;
    return [subject.CN, subject.SN, subject.G, subject.E].some((field) =>
        (field ?? "").toLowerCase().includes(needle),
    );
};

const filteredList = computed(() => {
    const needle = query.value.trim().toLowerCase();
    if (!showSearch.value || !needle) {
        return props.list;
    }
    return props.list.filter((certificate) => matchesQuery(certificate, needle));
});

const choose = (certificate) => {
    if (certificate.invalid) {
        if (certificate.invalidMessage) {
            notifyUser(certificate.invalidMessage);
        }
        return;
    }

    if (props.chosen?.thumbprint === certificate.thumbprint) {
        return;
    }

    emit("update:chosen", certificate);
};
</script>

<template>
    <div class="flex flex-col gap-5">
        <BaseSearchInput
            v-if="showSearch"
            v-model="query"
            label="Поиск по имени" />
        <p v-if="showSearch && !filteredList.length" class="text-dark-light text-sm">
            По вашему запросу ничего не найдено
        </p>
        <div v-else class="flex flex-col gap-5" role="radiogroup">
            <CertificateListItem
                v-for="certificate in filteredList"
                :key="certificate.thumbprint"
                :full="full"
                :selected="chosen?.thumbprint === certificate.thumbprint"
                :certificate="certificate"
                @click="choose(certificate)" />
        </div>
    </div>
</template>
